/* M2 (Ely 10-31): the countries the V2 catalog is narrowed to.

   Kept in the address bar's hash as `market=US,CA` beside the other hash keys (`#org=...`), so a link shares the
   selection, and remembered per user and account in localStorage for visits whose hash has none. Module state read
   through useSyncExternalStore, so the list hook and the picker share one selection without passing it down. */
import { useSyncExternalStore } from 'react';
import UserService from '@utility/services/user';
import { NO_COUNTRY, regionOfCountry } from '../lib/markets';

export type MarketSelection = {
  /** ISO country codes and NO_COUNTRY; empty means every marketplace. */
  countries: readonly string[];
  /** Narrow the list to items listed in those countries (default on). */
  onlyListed: boolean;
};

const HASH_PREFIX = 'market=';
const STORAGE_KEY = 'skupreme.catalogMarkets';
const EVERY_MARKET: MarketSelection = { countries: [], onlyListed: true };

/* Known codes only, each once; anything else in a hand-edited link is dropped. */
const parseCountries = (raw: string): string[] => {
  const codes = raw.split(',').map(token => {
    const code = token.trim();
    return code.toLowerCase() === NO_COUNTRY ? NO_COUNTRY : code.toUpperCase();
  });
  return [
    ...new Set(codes.filter(c => c === NO_COUNTRY || regionOfCountry(c))),
  ];
};

const hashParts = () =>
  window.location.hash.replace(/^#/, '').split('&').filter(Boolean);

/** The hash's countries, or null when it has no `market` key. */
const readHash = (): string[] | null => {
  const part = hashParts().find(p => p.startsWith(HASH_PREFIX));
  if (part === undefined) return null;
  try {
    return parseCountries(decodeURIComponent(part.slice(HASH_PREFIX.length)));
  } catch {
    return [];
  }
};

/* Past the router, like org-url.ts: nothing reads the hash through it. Other keys keep their order. */
const writeHash = (countries: readonly string[]) => {
  const others = hashParts().filter(p => !p.startsWith(HASH_PREFIX));
  const parts = countries.length
    ? [...others, `${HASH_PREFIX}${countries.join(',')}`]
    : others;
  const hash = parts.length ? `#${parts.join('&')}` : '';
  if (hash === window.location.hash) return;
  const { pathname, search } = window.location;
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${search}${hash}`,
  );
};

type StoredSession = { user?: { _id?: string; organization_id?: string } };

/* Per user and account (the signed-in organization). */
const storageKey = () => {
  const session = UserService.getUser() as StoredSession | null;
  return `${STORAGE_KEY}:${session?.user?._id ?? ''}:${session?.user?.organization_id ?? ''}`;
};

const readStorage = (): MarketSelection => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(storageKey()) ?? 'null',
    ) as Partial<MarketSelection> | null;
    return {
      countries: parseCountries((stored?.countries ?? []).join(',')),
      onlyListed: stored?.onlyListed !== false,
    };
  } catch {
    return EVERY_MARKET;
  }
};

const writeStorage = (selection: MarketSelection) => {
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(selection));
  } catch {
    /* no storage (private window): the hash still holds the countries */
  }
};

/* Read on first use, so a test or a new session starts from the address bar. */
let state: MarketSelection | undefined;
const listeners = new Set<() => void>();

const load = (): MarketSelection => {
  const stored = readStorage();
  const fromHash = readHash();
  return fromHash ? { ...stored, countries: fromHash } : stored;
};

const getState = (): MarketSelection => {
  state ??= load();
  return state;
};

/* A link pasted into this tab (hashchange) wins over what was picked. */
const onHashChange = () => {
  const fromHash = readHash();
  if (!fromHash || fromHash.join() === getState().countries.join()) return;
  state = { ...getState(), countries: fromHash };
  for (const listener of [...listeners]) listener();
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) window.addEventListener('hashchange', onHashChange);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('hashchange', onHashChange);
    }
  };
};

export function setMarketSelection(next: Partial<MarketSelection>) {
  state = { ...getState(), ...next };
  writeHash(state.countries);
  writeStorage(state);
  for (const listener of [...listeners]) listener();
}

/** Forgets the selection read, so the next reader starts from the hash and storage (tests, sign-out). */
export function resetMarketSelection() {
  state = undefined;
}

/** The selected countries and `onlyListed`, with setters; the list, the picker and the table read this one. */
export function useMarketSelection() {
  const selection = useSyncExternalStore(subscribe, getState, getState);
  return {
    ...selection,
    setCountries: (countries: readonly string[]) =>
      setMarketSelection({ countries }),
    setOnlyListed: (onlyListed: boolean) => setMarketSelection({ onlyListed }),
    clear: () => setMarketSelection({ countries: [] }),
  };
}
